import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Col, Row, Select, DatePicker } from 'antd';
import moment from 'moment';
import { DATE_FORMAT } from 'utils';
import { getListAction, getTripFilterList } from 'store/pageSlice';
import { Button } from 'atoms';
import {
	AverageTrips,
	InterCityTrips,
	TotalCities,
	RegularTrips,
	TotalProjects,
	TripCompleted,
	TotalVehicles,
	UnclosedTrips,
} from 'components/dashboard';
import { getDashboard } from 'store/dashboardSlice';

const MtdButton = styled(Button)`
	border-radius: 5px 0px 0px 5px;
	background: #9280fd;
	font-weight: 400;
	min-width: 80px;
`;

const TodayButton = styled(Button)`
	font-weight: 400;
	min-width: 90px;
	border-left: 1px solid #d9d9d9;
	border-right: 1px solid #d9d9d9;
`;

const FilterButton = styled(Button)`
	font-weight: 400;
	min-width: 90px;
	border-left: 1px solid #d9d9d9;
	border-right: 1px solid #d9d9d9;
`;

const { RangePicker } = DatePicker;
const RangePickerDate = styled(RangePicker)`
	border: none !important;
`;

const PrimaryCostSelect = styled(Select)`
	width: 150px;
	margin-left: 30px;
`;

const DateTimeFormat = 'MM/DD/YYYY HH:mm';

const getStartTimeFormat = (mtdStartDate) => {
	const fomatedFromDate = moment.utc(mtdStartDate).format(DATE_FORMAT);
	const validStartData = `${fomatedFromDate} 00:00`;
	return moment.utc(validStartData).subtract(330, 'minutes').format(DateTimeFormat);
};

const getEndTimeFormat = (mtdEndDate) => {
	const fomatedToDate = moment.utc(mtdEndDate).format(DATE_FORMAT);
	const validEndData = `${fomatedToDate} 23:59`;
	return moment.utc(validEndData).subtract(330, 'minutes').format(DateTimeFormat);
};

const primaryCostOptions = [
	{ value: 'logistics', label: 'logistics' },
	{ value: 'distribution', label: 'distribution' },
];

function ClientTab() {
	const dispatch = useDispatch();
	const [mtdToggle, setMtdToggle] = useState(true);
	const [todayToggle, setTodayToggle] = useState(false);
	const [dateRangeToggle, setDateRangeToggle] = useState(false);
	const [datePickerVal, setDatePickerVal] = useState(['', '']);
	const [citiesFilterData, setCitiesFilterData] = useState([]);
	const [businessUnitsFilterData, setBusinessUnitsFilterData] = useState([]);
	const [projectFilterData, setProjectFilterData] = useState([]);
	const filterOption = (input, option) =>
		(option?.label ?? '').toLowerCase().includes(input.toLowerCase());
	const [selectedProjects, setSelectedProjects] = useState([]);
	const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);
	const [finalFilterData, setFinalFilterData] = useState({
		start_time: getStartTimeFormat(moment().startOf('month').format(DATE_FORMAT)),
		end_time: getEndTimeFormat(moment().format(DATE_FORMAT)),
	});
	const [filterData, setFilterData] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [dashboardData, setDashboardData] = useState([]);
	const [primaryCostCenter, setPrimaryCostCenter] = useState(null);

	useEffect(() => {
		const { start_time, end_time } = finalFilterData;
		setFilterData({
			...filterData,
			start_time,
			end_time,
		});
	}, [finalFilterData]);

	useEffect(() => {
		hanndleFilterChange();
		const mtdStartDate = moment().startOf('month').format(DATE_FORMAT);
		const mtdEndDate = moment().format(DATE_FORMAT);

		setFinalFilterData({
			start_time: getStartTimeFormat(mtdStartDate),
			end_time: getEndTimeFormat(mtdEndDate),
		});
	}, []);

	useEffect(() => {
		const filterBy = {};

		if (filterData?.business_unit_ids && filterData.business_unit_ids.length > 0) {
			filterBy.business_unit_ids = filterData.business_unit_ids;
		}

		if (filterData?.cities && filterData.cities.length > 0) {
			filterBy.cities = filterData.cities;
		}

		dispatch(
			getListAction({
				search_type: 'project',
				offset: 0,
				limit: 100,
				...filterBy,
			})
		).then((res) => {
			const dataProject =
				res?.rows?.map((item) => ({
					value: item?.id,
					label: item?.project_location_code,
					item,
				})) || [];
			setProjectFilterData(dataProject);
		});
	}, [dispatch, filterData?.business_unit_ids, filterData?.cities]);

	useEffect(() => {
		dispatch(getTripFilterList('city')).then((res) => {
			const cities = res?.rows?.cities?.map((element) => ({
				value: element,
				label: element,
			}));
			setCitiesFilterData(cities);
		});
	}, [dispatch]);

	useEffect(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
			})
		).then((res) => {
			const buOptions = (res?.rows || []).map((bu) => ({
				value: bu.id,
				label: bu.name,
			}));
			setBusinessUnitsFilterData(buOptions);
		});
	}, [dispatch]);

	useEffect(() => {
		setIsLoading(true);
		dispatch(
			getDashboard({
				dashboard_type: 'client',
				filter_by: {
					...finalFilterData,
					...(primaryCostCenter && { primary_cost_center: primaryCostCenter }),
				},
			})
		)
			.then((res) => {
				setDashboardData(res);
			})
			.finally(() => setIsLoading(false));
	}, [finalFilterData, primaryCostCenter, dispatch]);

	const dateformat = 'YYYY-MM-DD';

	function disableDateRanges(current) {
		const todayDate = moment(new Date());
		const PAST_RANGE = 365;

		const startDate = moment().subtract(PAST_RANGE, 'days');

		const startCheck = current && current < moment(startDate, dateformat);
		const endCheck = current && current > moment().endOf(todayDate, dateformat);

		return startCheck || endCheck;
	}
	const hanndleFilterChange = (
		mtdStartDate = moment().startOf('month').format(DATE_FORMAT),
		mtdEndDate = moment().format(DATE_FORMAT)
	) => {
		setIsLoading(false);
		setFilterData({
			start_time: getStartTimeFormat(mtdStartDate),
			end_time: getEndTimeFormat(mtdEndDate),
		});
	};

	const clearAllFilters = () => {
		setSelectedProjects([]);
		setSelectedBusinessUnits([]);
		setFilterData({
			start_time: filterData.start_time,
			end_time: filterData.end_time,
		});
	};

	return (
		<>
			<Row style={{ marginBottom: '1rem' }}>
				<Col style={{ display: 'flex', gap: 0, alignItems: 'center' }}>
					<div>
						<MtdButton
							style={{
								background: mtdToggle ? '#9280fd' : '#fff',
								color: mtdToggle ? '#fff' : '#000',
							}}
							onClick={() => {
								setMtdToggle(true);
								setTodayToggle(false);
								setDateRangeToggle(false);
								setDatePickerVal(['', '']);
								clearAllFilters();
								setFinalFilterData({
									start_time: getStartTimeFormat(
										moment().startOf('month').format(DATE_FORMAT)
									),
									end_time: getEndTimeFormat(moment().format(DATE_FORMAT)),
								});
							}}
						>
							MTD
						</MtdButton>
					</div>
					<div>
						<TodayButton
							style={{
								background: todayToggle ? '#9280fd' : '#fff',
								color: todayToggle ? '#fff' : '#000',
								margin: 0,
							}}
							onClick={() => {
								setMtdToggle(false);
								setTodayToggle(true);
								setDateRangeToggle(false);
								setDatePickerVal(['', '']);
								clearAllFilters();
								setFinalFilterData({
									start_time: getStartTimeFormat(
										moment().startOf('day').format(DATE_FORMAT)
									),
									end_time: getEndTimeFormat(moment().format(DATE_FORMAT)),
								});
							}}
						>
							Today
						</TodayButton>
					</div>
					<div>
						<RangePickerDate
							style={{
								background: dateRangeToggle ? '#e2ddff' : '#fff',
							}}
							disabledDate={(current) => disableDateRanges(current)}
							name="date_filter"
							allowClear
							value={datePickerVal}
							onChange={(val) => {
								if (val) {
									setDatePickerVal(val);
									setMtdToggle(false);
									setTodayToggle(false);
									setDateRangeToggle(true);
									clearAllFilters();
									setFinalFilterData({
										start_time: getStartTimeFormat(
											moment(val[0]).startOf('day').format(DATE_FORMAT)
										),
										end_time: getEndTimeFormat(
											moment(val[1]).format(DATE_FORMAT)
										),
									});
								} else {
									setDatePickerVal(['', '']);
									hanndleFilterChange();
									setMtdToggle(true);
									setDateRangeToggle(false);
									clearAllFilters();
									setFinalFilterData({
										start_time: getStartTimeFormat(
											moment().startOf('month').format(DATE_FORMAT)
										),
										end_time: getEndTimeFormat(moment().format(DATE_FORMAT)),
									});
								}
							}}
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
						/>
					</div>
					<PrimaryCostSelect
						placeholder="Primary Cost Center"
						options={primaryCostOptions}
						onChange={(value) => setPrimaryCostCenter(value)}
						allowClear
					/>
				</Col>
				<Col style={{ marginLeft: '30px' }}>
					<Select
						mode="multiple"
						allowClear
						style={{ width: '15rem' }}
						placeholder="Business Unit"
						value={selectedBusinessUnits}
						onChange={(val) => {
							setSelectedBusinessUnits(val);
							setSelectedProjects([]);
							if (val.length > 0) {
								const { projects, ...reset } = filterData;
								setFilterData({
									...reset,
									business_unit_ids: val,
								});
							} else {
								const { business_unit_ids, projects, ...rest } = filterData;
								setFilterData(rest);
								setFinalFilterData(rest);
							}
						}}
						options={businessUnitsFilterData}
						filterOption={filterOption}
						maxTagCount="responsive"
					/>
				</Col>
				<Col style={{ marginLeft: '30px' }}>
					<Select
						mode="multiple"
						allowClear
						style={{ width: '15rem' }}
						placeholder="City"
						onChange={(val) => {
							setSelectedProjects([]);
							setSelectedBusinessUnits([]);
							if (val.length > 0) {
								const { projects, business_unit_ids, ...reset } = filterData;
								setFilterData({
									...reset,
									cities: val,
								});
							} else {
								const { cities, projects, business_unit_ids, ...rest } = filterData;
								setFilterData(rest);
								setFinalFilterData(rest);
							}
						}}
						options={citiesFilterData}
						filterOption={filterOption}
						maxTagCount="responsive"
					/>
				</Col>
				<Col style={{ marginLeft: '30px' }}>
					<Select
						mode="multiple"
						allowClear
						style={{ width: '23rem' }}
						placeholder="Project"
						disabled={!filterData?.cities && !filterData?.business_unit_ids}
						onChange={(val) => {
							setSelectedProjects(val);
							if (val.length > 0) {
								setFilterData({
									...filterData,
									projects: val,
								});
							} else {
								const { projects, ...rest } = filterData;
								setFilterData(rest);
							}
						}}
						value={selectedProjects}
						options={projectFilterData}
						filterOption={filterOption}
						maxTagCount="responsive"
					/>
				</Col>

				<Col>
					<FilterButton
						disabled={!filterData?.cities && !filterData?.business_unit_ids}
						onClick={() => {
							setFinalFilterData(filterData);
						}}
					>
						Apply Filter
					</FilterButton>
				</Col>
			</Row>
			<Row align="stretch" gutter={[16, 16]}>
				<Col xs={24} sm={24} md={9}>
					<TripCompleted completed={dashboardData?.completed} isLoading={isLoading} />
				</Col>
				<Col xs={24} sm={24} md={15}>
					<Row style={{ height: '100%' }} align="stretch" gutter={[16, 16]}>
						<Col xs={24} sm={24} md={12}>
							<UnclosedTrips
								unclosed={dashboardData?.unclosed}
								isLoading={isLoading}
							/>
						</Col>
						<Col xs={24} sm={24} md={12}>
							<InterCityTrips cities={dashboardData?.cities} isLoading={isLoading} />
						</Col>
						<Col xs={24}>
							<RegularTrips
								regularTrips={dashboardData?.trend}
								isLoading={isLoading}
							/>
						</Col>
					</Row>
				</Col>
				<Col sm={24}>
					<Row align="stretch" gutter={[16, 16]}>
						<Col xs={24} sm={12} lg={6}>
							<AverageTrips
								averageTrips={dashboardData?.average}
								isLoading={isLoading}
							/>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<TotalVehicles
								vehicles={dashboardData?.vehicles}
								isLoading={isLoading}
							/>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<TotalProjects
								projects_count={dashboardData?.project_count}
								isLoading={isLoading}
							/>
						</Col>
						<Col xs={24} sm={12} lg={6}>
							<TotalCities totalCities={dashboardData.cities} isLoading={isLoading} />
						</Col>
					</Row>
				</Col>
			</Row>
		</>
	);
}

export default ClientTab;
